import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChartLine, Shield, Lightning, ArrowRight, TrendUp, Target, FileText, Bell,
  GraduationCap, House, FirstAid, Heartbeat, Umbrella, Money, Airplane, Briefcase,
  Phone, Envelope, MapPin, CaretDown, CaretUp,
} from "@phosphor-icons/react";
import { useState } from "react";

const LOGO_URL = "/gv_logo.png";
const INVESTWELL_URL = "https://guliventures.investwell.app/";

const goals = [
  { icon: GraduationCap, title: "International Education" },
  { icon: House, title: "New Home" },
  { icon: FirstAid, title: "Health Care" },
  { icon: Heartbeat, title: "Retirement Living" },
  { icon: Shield, title: "Protection from Unforeseen Events" },
  { icon: GraduationCap, title: "College / Higher Education" },
  { icon: Money, title: "Emergency Funds" },
  { icon: Airplane, title: "Travel / Vacation" },
  { icon: Briefcase, title: "Start Business" },
  { icon: House, title: "Home Improvement" },
  { icon: Target, title: "Wedding in the Family" },
  { icon: Money, title: "Planned Major Expenses" },
];

const features = [
  { icon: ChartLine, title: "XIRR Performance", desc: "Annualized return calculations with real-time NAV data from AMFI for all your mutual funds." },
  { icon: TrendUp, title: "Market Indices", desc: "Live Nifty 50, Sensex, Gift Nifty, Mid Cap, Small Cap, Dow Jones & NASDAQ tracking." },
  { icon: Target, title: "Smart Analytics", desc: "Sector gap analysis, fund overlap detection, and SIP pattern recognition." },
  { icon: Shield, title: "Fund Monitoring", desc: "Instant alerts on fund manager changes, category shifts, and objective updates." },
  { icon: FileText, title: "Tax Reports", desc: "Auto-generated STCG/LTCG capital gains reports with PDF export for ITR filing." },
  { icon: Lightning, title: "Daily NAV Updates", desc: "Automated portfolio revaluation with live NAV fetched daily from AMFI India." },
];

const stats = [
  { value: "10+", label: "Years of Experience" },
  { value: "40+", label: "AMC Partners" },
];

const faqs = [
  {
    q: "How do I track my mutual fund portfolio?",
    a: "Simply upload your CAS (Consolidated Account Statement) from CAMS or KFintech, and our system will automatically parse all your transactions, calculate XIRR, and show you real-time portfolio performance."
  },
  {
    q: "Is my financial data secure?",
    a: "Absolutely. Your data is encrypted and stored securely. We never share your financial information with third parties. You are always the rightful owner of your investment data."
  },
  {
    q: "What is XIRR and why is it important?",
    a: "XIRR (Extended Internal Rate of Return) is the most accurate measure of your investment returns as it accounts for the exact timing and amount of each investment. Unlike simple returns, XIRR gives you the true annualized return on your SIPs and lumpsum investments."
  },
  {
    q: "How often is NAV data updated?",
    a: "NAV data is automatically refreshed daily from AMFI India. Your portfolio valuation is always up-to-date with the latest available NAV."
  },
  {
    q: "Can I generate tax reports for ITR filing?",
    a: "Yes! Our platform auto-generates STCG (Short Term Capital Gains) and LTCG (Long Term Capital Gains) reports using FIFO method, ready for export as PDF for your tax filing."
  },
  {
    q: "How much does Guli Ventures charge for its services?",
    a: "We do not charge anything directly from our clients. We earn our revenue from mutual fund companies. Our portfolio tracking platform is available free of cost to all our clients."
  },
];

const processSteps = [
  { num: "1", title: "Understand", desc: "We understand your risk profile and financial goals in detail" },
  { num: "2", title: "Plan", desc: "Your major financial goals are listed and asset allocation is planned" },
  { num: "3", title: "Invest", desc: "Investments are earmarked for each goal with the right fund selection" },
  { num: "4", title: "Review", desc: "Regular portfolio review to ensure you are on track to meet your goals" },
];

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#5a1a3d]/50">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left group">
        <span className="text-base font-medium text-[#F5EDE3] group-hover:text-[#d7be69] transition-colors pr-4">{q}</span>
        {open ? <CaretUp size={20} className="text-[#d7be69] flex-shrink-0" /> : <CaretDown size={20} className="text-[#8A7468] flex-shrink-0" />}
      </button>
      {open && <p className="text-sm text-[#C4B09A] leading-relaxed pb-5 pr-8">{a}</p>}
    </div>
  );
};

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#2c041c]" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[#2c041c]/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Guli Ventures" className="h-14 w-auto object-contain" />
          </Link>
          <div className="flex items-center gap-3">
            <a href={INVESTWELL_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-client-login-btn"
              className="bg-[#d7be69] text-[#260F1A] px-5 py-2 text-sm font-semibold hover:bg-[#E0C88B] transition-colors rounded">
              Client Login
            </a>
            <a href="#contact" data-testid="nav-contact-btn"
              className="border border-[#d7be69] text-[#d7be69] px-5 py-2 text-sm font-semibold hover:bg-[#d7be69]/10 transition-colors rounded hidden sm:inline-block">
              Contact Us
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#3d0a28] to-[#2c041c]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="text-center max-w-4xl mx-auto">
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#d7be69] mb-6">AMFI Registered Mutual Fund Distributor | ARN - 358908</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#F5EDE3] tracking-tight leading-tight mb-8"
                style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="hero-heading">
              "We take a holistic approach to your personal finance using creative solutions to help you manage your financial affairs"
            </h1>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href={INVESTWELL_URL} target="_blank" rel="noopener noreferrer"
                className="bg-[#d7be69] text-[#260F1A] px-8 py-3.5 font-semibold hover:bg-[#E0C88B] transition-colors inline-flex items-center gap-2 rounded">
                Get Started <ArrowRight size={18} weight="bold" />
              </a>
              <a href="#services"
                className="border border-[#d7be69]/50 text-[#d7be69] px-8 py-3.5 font-semibold hover:bg-[#d7be69]/10 transition-colors rounded">
                Our Services
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Welcome */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-[#d7be69] mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Welcome to Guli Ventures
          </h2>
          <p className="text-base text-[#C4B09A] leading-relaxed">
            At Guli Ventures we are committed to delivering better value, choice and results for our clients. We use a goals-based approach that takes account of your personal needs, with time frames to achieve these, and the level of risk you wish to take. We then blend all of this information to create your personal strategy and financial roadmap.
          </p>
        </motion.div>
      </section>

      {/* Goals */}
      <section className="bg-[#3d0a28]/50 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-semibold text-[#F5EDE3] text-center mb-12" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Whatever your Goals be, we are here to help!
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {goals.map((g, i) => (
              <motion.div key={g.title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="bg-[#2c041c] border border-[#5a1a3d] rounded-xl p-5 text-center hover:border-[#d7be69]/40 hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 mx-auto mb-3 bg-[#d7be69]/10 rounded-full flex items-center justify-center">
                  <g.icon size={24} weight="duotone" className="text-[#d7be69]" />
                </div>
                <p className="text-xs font-medium text-[#C4B09A] leading-tight">{g.title}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Credibility Stats */}
      <section className="bg-[#d7be69] py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 text-center max-w-lg mx-auto">
            {stats.map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                transition={{ delay: i * 0.1 }} data-testid={`stat-${s.label.replace(/\s+/g, '-').toLowerCase()}`}>
                <p className="text-4xl md:text-5xl font-bold text-[#2c041c]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{s.value}</p>
                <p className="text-sm font-medium text-[#3d0a28] mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer / Features */}
      <section id="services" className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <div className="text-center mb-12">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#d7be69] mb-4">What We Offer</p>
          <h2 className="text-3xl md:text-4xl font-semibold text-[#F5EDE3] tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Institutional-Grade Tools, Personal Portfolio
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div key={f.title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="bg-[#3d0a28] border border-[#5a1a3d] rounded-xl p-8 hover:border-[#d7be69]/40 hover:shadow-lg transition-all duration-300 group"
              data-testid={`feature-card-${i}`}>
              <div className="w-12 h-12 bg-[#d7be69]/10 rounded-lg flex items-center justify-center mb-5 group-hover:bg-[#d7be69]/20 transition-colors">
                <f.icon size={24} weight="duotone" className="text-[#d7be69]" />
              </div>
              <h3 className="text-lg font-medium text-[#F5EDE3] mb-2">{f.title}</h3>
              <p className="text-sm text-[#C4B09A] leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why Choose Us - Process Steps */}
      <section className="bg-[#3d0a28]/50 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-semibold text-[#d7be69] text-center mb-12" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Why Choose Us
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {processSteps.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="text-center">
                <div className="w-14 h-14 mx-auto mb-4 bg-[#d7be69] rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-[#2c041c]">{s.num}</span>
                </div>
                <h3 className="text-lg font-semibold text-[#F5EDE3] mb-2">{s.title}</h3>
                <p className="text-sm text-[#C4B09A] leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <h2 className="text-3xl md:text-4xl font-semibold text-[#d7be69] text-center mb-10" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Frequently Asked Questions
        </h2>
        <div data-testid="faq-section">
          {faqs.map((f) => <FAQItem key={f.q} q={f.q} a={f.a} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#d7be69] py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-[#2c041c] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Ready to Start Your Investment Journey?
          </h2>
          <p className="text-[#3d0a28] text-base max-w-2xl mx-auto mb-8">
            With so many options, investing with us is simpler and more straightforward than ever before.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href={INVESTWELL_URL} target="_blank" rel="noopener noreferrer"
              className="bg-[#2c041c] text-[#d7be69] px-8 py-3.5 font-semibold hover:bg-[#3d0a28] transition-colors inline-flex items-center gap-2 rounded">
              Get Started <ArrowRight size={18} weight="bold" />
            </a>
            <a href="#contact"
              className="border-2 border-[#2c041c] text-[#2c041c] px-8 py-3.5 font-semibold hover:bg-[#2c041c]/10 transition-colors rounded">
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#1a0610] border-t border-[#5a1a3d]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
            {/* About */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="Guli Ventures" className="h-14 w-auto object-contain" />
              </div>
              <p className="text-sm text-[#8A7468] leading-relaxed">
                Your trusted partner for mutual fund investments. We help individuals and families achieve their financial goals through disciplined, goal-based investing.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-[#d7be69] mb-4">Quick Links</h4>
              <ul className="space-y-2.5">
                <li><a href={INVESTWELL_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-[#C4B09A] hover:text-[#d7be69] transition-colors">Client Login</a></li>
                <li><a href="#services" className="text-sm text-[#C4B09A] hover:text-[#d7be69] transition-colors">Our Services</a></li>
                <li><a href="https://www.amfiindia.com/investor/knowledge-center-info?zoneName=riskInMutualFunds" target="_blank" rel="noopener noreferrer" className="text-sm text-[#C4B09A] hover:text-[#d7be69] transition-colors">AMFI Risk Factors</a></li>
              </ul>
            </div>

            {/* Our Solutions */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-[#d7be69] mb-4">Our Solutions</h4>
              <ul className="space-y-2.5">
                <li><span className="text-sm text-[#C4B09A]">Mutual Funds</span></li>
                <li><span className="text-sm text-[#C4B09A]">Fixed Deposits</span></li>
                <li><span className="text-sm text-[#C4B09A]">Tax Planning</span></li>
                <li><span className="text-sm text-[#C4B09A]">Retirement Solutions</span></li>
                <li><span className="text-sm text-[#C4B09A]">Family Protection</span></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-[#d7be69] mb-4">Get In Touch</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-2.5">
                  <MapPin size={16} className="text-[#d7be69] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-[#C4B09A]">16/7, D-45/5, 2nd Floor, Old Santhepet, Mysuru - 570001, Karnataka</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <Phone size={16} className="text-[#d7be69] flex-shrink-0" />
                  <a href="tel:8866666108" className="text-sm text-[#C4B09A] hover:text-[#d7be69] transition-colors">+91-8866666108</a>
                </li>
                <li className="flex items-center gap-2.5">
                  <Envelope size={16} className="text-[#d7be69] flex-shrink-0" />
                  <a href="mailto:suhasguli@guliventures.com" className="text-sm text-[#C4B09A] hover:text-[#d7be69] transition-colors">suhasguli@guliventures.com</a>
                </li>
              </ul>
            </div>
          </div>

          {/* AMFI Disclaimer */}
          <div className="border-t border-[#5a1a3d]/50 pt-8">
            <p className="text-[11px] text-[#6B5A52] leading-relaxed mb-4">
              <strong className="text-[#8A7468]">Risk Factors</strong> – Investments in Mutual Funds are subject to Market Risks. Read all scheme related documents carefully before investing. Mutual Fund Schemes do not assure or guarantee any returns. Past performances of any Mutual Fund Scheme may or may not be sustained in future. There is no guarantee that the investment objective of any suggested scheme shall be achieved. All existing and prospective investors are advised to check and evaluate the Exit loads and other cost structure (TER) applicable at the time of making the investment before finalizing on any investment decision for Mutual Funds schemes. We deal in Regular Plans only for Mutual Fund Schemes and earn a Trailing Commission on client investments. Disclosure for commission earnings is made to clients at the time of investments. Option of Direct Plan for every Mutual Fund Scheme is available to investors offering advantage of lower expense ratio. We are not entitled to earn any commission on Direct Plans. Hence we do not deal in Direct Plans.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-[11px] text-[#8A7468]">
                AMFI Registered Mutual Fund Distributor | ARN - 358908
              </p>
              <p className="text-[11px] text-[#6B5A52]">
                Grievance Officer: Suhas Guli | suhasguli@guliventures.com | +91-8866666108
              </p>
            </div>
            <p className="text-[11px] text-[#6B5A52] mt-3 text-center">
              &copy; {new Date().getFullYear()} Guli Ventures. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
